import { Drawable } from "./drawable";
import { ScreenState } from "./screen";

export class BasicText extends Drawable{
  /**
  * Initialiases the Static Text drawable.
  * @param {number} x A positive number indicating x coordinate on screen
  * @param {number} y A positive number indicating y coordinate on screen
  * @param {string} text The text to render
  * @param {string | CanvasGradient | CanvasPattern} colour The text color
  * @param {boolean} [configureCanvas=true] Whether to configure canvas before render
  * @param {number} [fontSize=48] The font size
  * @param {string} [font='Yusei Magic'] The text font
  * @param {CanvasTextAlign} [textAlign='left'] The text alignment
  * @param {CanvasTextBaseline} [textBaseline='alphabetic'] The text baseline
  */
  constructor(x, y, text, colour, configureCanvas, fontSize, font, textAlign, textBaseline){
    super(x, y, configureCanvas);
    this.text = text;
    this.fontSize = fontSize || 48;
    this.colour = colour || 'blue'
    this.font = font || 'Yusei Magic';
    this.textAlign = textAlign || 'left';
    this.textBaseline = textBaseline || 'alphabetic';
  }

  /**
  * Configures the canvas to render the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  configure(screenState){
    screenState.canvasCtx.fillStyle = this.colour;
    screenState.canvasCtx.font = `${screenState.S(this.fontSize*screenState.transitionProgress)}px ${this.font}`;
    screenState.canvasCtx.textAlign = this.textAlign;
    screenState.canvasCtx.textBaseline = this.textBaseline;
  }

  /**
  * Renders the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  render(screenState) {
    super.render(screenState);
    screenState.canvasCtx.fillText(this.text, screenState.X(this.x), screenState.Y(this.y)); 
  }
}

export class Text extends BasicText{
  /**
  * Initialiases the Static Text drawable.
  * @param {number} x A positive number indicating x coordinate on screen
  * @param {number} y A positive number indicating y coordinate on screen
  * @param {string} text The text to render
  * @param {string | CanvasGradient | CanvasPattern} [colour='blue'] The text color
  * @param {string | CanvasGradient | CanvasPattern} [shadowColour='yellow'] The text shadow color
  * @param {boolean} [configureCanvas=true] Whether to configure canvas before render
  * @param {number} [fontSize=48] The font size
  * @param {string} [font='Yusei Magic'] The text font
  * @param {CanvasTextAlign} [textAlign='left'] The text alignment
  * @param {CanvasTextBaseline} [textBaseline='alphabetic'] The text baseline
  */
  constructor(x, y, text, colour, shadowColour, configureCanvas, fontSize, font, textAlign, textBaseline){
    super(x, y, configureCanvas);
    this.text = text;
    this.fontSize = fontSize || 48;
    this.colour = colour || 'blue';
    this.shadowColour = shadowColour || 'yellow';
    this.font = font || 'Yusei Magic';
    this.textAlign = textAlign || 'left';
    this.textBaseline = textBaseline || 'alphabetic';
  }

  /**
  * Configures the canvas to render the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  configure(screenState){
    screenState.canvasCtx.fillStyle = this.colour;
    screenState.canvasCtx.strokeStyle = 'white';
    screenState.canvasCtx.shadowColor = this.shadowColour;
    screenState.canvasCtx.shadowBlur = 15;
    screenState.canvasCtx.font = `${screenState.S(this.fontSize*screenState.transitionProgress)}px ${this.font}`;
    screenState.canvasCtx.textAlign = this.textAlign;
    screenState.canvasCtx.textBaseline = this.textBaseline;
  }

  /**
  * Renders the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  render(screenState) {
    super.render(screenState);
    const [xPos, yPos] = [screenState.X(this.x), screenState.Y(this.y)];
    // screenState.canvasCtx.beginPath();
    // console.log(this.colour);
    screenState.canvasCtx.globalAlpha = screenState.transitionProgress;
    screenState.canvasCtx.fillStyle = this.colour;
    screenState.canvasCtx.fillText(this.text, xPos, yPos); 
    screenState.canvasCtx.lineWidth = 3;
    screenState.canvasCtx.strokeText(this.text, xPos, yPos);
    screenState.canvasCtx.shadowBlur = 0;
    screenState.canvasCtx.globalAlpha = 1;
  }
}



export class AnimatedText extends Drawable{
  /**
  * Initialiases the Animated Text drawable.
  * @param {number} x A positive number indicating x coordinate on screen.
  * @param {number} y A positive number indicating y coordinate on screen.
  * @param {string} text The text to render.
  * @param {"ltr" | "rtl"} animationDirection The direction in which the text should be animated.
  * @param {number} [targetFontSize] The target font size to animate to.
  * @param {boolean} [destroyOnFinish=true] Destroy self when animation finishes.
  * @param {boolean} [configureCanvas=true] Whether to configure canvas before render
  * @param {string} [font='Yusei Magic'] The text font
  */
  constructor(x, y, text, animationDirection, targetFontSize, configureCanvas, font){
    super(x, y, configureCanvas);
    this.text = text;
    this.animationDirection = animationDirection;
    this.targetFontSize = targetFontSize;
    this.font = font || 'Yusei Magic';
    // this.animationProgress = 0;
  }

  /**
   * @param {string} value
   */
  set text(value){
    this.textParsed = value.split("");
  }

  get text(){
    return this.textParsed.join("");
  }

  /**
  * Configures the canvas to render the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  configure(screenState){
    screenState.canvasCtx.textBaseline = 'middle';
    screenState.canvasCtx.textAlign = this.animationDirection == 'ltr' ? 'left' : 'right';
  }

  /**
  * Renders the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  render(screenState) {
    super.render(screenState);
    // const animationProgress = Math.abs(Math.sin(screenState.animationStep/500));
    // const animationOffset = (1/this.textParsed.length)*(1-animationProgress);
    if (this.animationDirection == 'ltr'){
      for (let i=this.textParsed.length-1; i >= 0; i--){// Iterate, backwards, allow end of text to not overlap first character
        const fontSize = screenState.S(Math.abs(Math.sin((screenState.animationStep + i*50)/500))*this.targetFontSize*screenState.transitionProgress);
        screenState.canvasCtx.font = `${fontSize}px ${this.font}`;
        const xPos = screenState.X(this.x + (i * fontSize));
        const yPos = screenState.Y(this.y);
        screenState.canvasCtx.strokeText(this.textParsed[i], xPos, yPos);
        screenState.canvasCtx.fillText(this.textParsed[i], xPos, yPos);
      }
    }
    else{
      const lastCharOffsetXPos = this.textParsed.length*this.targetFontSize; 
      for (let i=0; i < this.textParsed.length; i++){
        const fontSize = screenState.S(Math.abs(Math.sin((screenState.animationStep + i*50)/500))*this.targetFontSize*screenState.transitionProgress);
        screenState.canvasCtx.font = `${fontSize}px ${this.font}`;
        const xPos = screenState.X(this.x + lastCharOffsetXPos - ((this.textParsed.length - 1 - i)*fontSize));
        const yPos = screenState.Y(this.y);
        screenState.canvasCtx.strokeText(this.textParsed[i], xPos, yPos);
        screenState.canvasCtx.fillText(this.textParsed[i], xPos, yPos);
      }
    }
  }
}